import React, { useCallback, useRef, useState } from 'react';

import {
  alpha,
  Button,
  CircularProgress,
  Paper,
  useMediaQuery,
  useTheme,
} from '@mui/material';

import { Icon, Stack, Typography } from '@liscio/ui';

import { FILE_UPLOAD_ACCEPT } from './constants';
import { ProcessingLaneAnimation } from './ProcessingLaneAnimation';

export type FileIntelligenceUploadZoneProps = {
  selectedFile: File | null;
  isReading: boolean;
  disabled?: boolean;
  onFileSelected: (file: File) => void;
};

export const FileIntelligenceUploadZone: React.FC<
  FileIntelligenceUploadZoneProps
> = ({ selectedFile, isReading, disabled = false, onFileSelected }) => {
  const theme = useTheme();
  const prefersReducedMotion = useMediaQuery('(prefers-reduced-motion: reduce)');
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [isDragOver, setIsDragOver] = useState(false);
  const dragCounterRef = useRef(0);

  const handleChooseFile = () => fileInputRef.current?.click();

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      onFileSelected(file);
    }
    event.target.value = '';
  };

  const handleDragEnter = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (selectedFile || disabled) return;
    dragCounterRef.current += 1;
    setIsDragOver(true);
  }, [disabled, selectedFile]);

  const handleDragLeave = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (selectedFile || disabled) return;
    dragCounterRef.current -= 1;
    if (dragCounterRef.current <= 0) {
      dragCounterRef.current = 0;
      setIsDragOver(false);
    }
  }, [disabled, selectedFile]);

  const handleDragOver = useCallback((event: React.DragEvent) => {
    event.preventDefault();
    event.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (event: React.DragEvent) => {
      event.preventDefault();
      event.stopPropagation();
      dragCounterRef.current = 0;
      setIsDragOver(false);
      if (selectedFile || disabled) return;

      const file = event.dataTransfer.files?.[0];
      if (file) {
        onFileSelected(file);
      }
    },
    [disabled, onFileSelected, selectedFile]
  );

  const showFileRow = Boolean(selectedFile);

  return (
    <Paper
      variant="outlined"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
      sx={{
        p: 2.5,
        mb: 2,
        borderRadius: 1,
        borderStyle: 'dashed',
        borderColor: isDragOver ? 'secondary.main' : 'grey.400',
        bgcolor: isDragOver
          ? alpha(theme.palette.secondary.main, 0.06)
          : 'transparent',
        textAlign: 'center',
        transition: theme.transitions.create(['border-color', 'background-color'], {
          duration: theme.transitions.duration.short,
        }),
      }}
    >
      {showFileRow ? (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1.5}
          sx={{ py: 2, px: 1 }}
        >
          <Icon icon="fileLines" sx={{ fontSize: 22, color: 'text.secondary', flexShrink: 0 }} />
          <Typography
            variant="body2"
            fontWeight={600}
            sx={{
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              maxWidth: { xs: 160, sm: 280 },
            }}
          >
            {selectedFile?.name}
          </Typography>
          {isReading && (
            <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
              {!prefersReducedMotion && (
                <CircularProgress size={14} thickness={5} color="secondary" />
              )}
              <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                Reading document…
              </Typography>
            </Stack>
          )}
        </Stack>
      ) : (
        <>
          <ProcessingLaneAnimation
            isDragOver={isDragOver}
            prefersReducedMotion={prefersReducedMotion}
          />

          <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.25 }}>
            Upload a client file
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
            Try a tax return, W-2, 1099, bank statement, invoice, or K-1.
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1.5, display: 'block' }}>
            Supported: PDF, PNG, JPEG (max 100 MB)
          </Typography>

          <input
            ref={fileInputRef}
            type="file"
            accept={FILE_UPLOAD_ACCEPT}
            hidden
            onChange={handleFileChange}
          />
          <Button
            variant="contained"
            onClick={handleChooseFile}
            disabled={disabled}
            sx={{ textTransform: 'none', minWidth: 160 }}
          >
            Choose file
          </Button>
        </>
      )}
    </Paper>
  );
};

export default FileIntelligenceUploadZone;
